import React, { useState, useEffect, useRef } from "react";
import axios from "axios";

const API = "/api";

function fmtEta(sec: number | null): string {
  if (sec == null) return "—";
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60), s = sec % 60;
  if (m < 60) return `${m}m ${s}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

export default function ImportSharedModal({ currentUser, onClose, onDone, asPanel = false }: any) {
  const isAdmin = currentUser?.role === "admin";
  const [accountName, setAccountName] = useState("");
  const [status, setStatus] = useState<any>(null);
  const [error, setError] = useState("");
  const [starting, setStarting] = useState(false);
  const pollRef = useRef<any>(null);

  const fetchStatus = async () => {
    try {
      const r = await axios.get(API + "/offers/import-shared/status");
      if (r.data?.exists) {
        setStatus(r.data);
        if (r.data.status === "done" || r.data.status === "error") {
          if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
          if (r.data.status === "done" && onDone) onDone();
        }
      }
    } catch (e) { /* ignoruj bledy pollingu */ }
  };

  useEffect(() => {
    fetchStatus();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line
  }, []);

  const startImport = async () => {
    setError(""); setStarting(true);
    try {
      await axios.post(API + "/offers/import-shared/start", { account_name: accountName.trim() });
      if (!pollRef.current) pollRef.current = setInterval(fetchStatus, 4000);
      await fetchStatus();
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Blad startu importu");
    } finally { setStarting(false); }
  };

  const running = status && (status.status === "pending" || status.status === "running");

  const renderContent = () => (
    <>
      <h2 style={{ marginTop: 0, fontSize: 18 }}>📥 Importuj oferty kontrahenta</h2>

      {!isAdmin && (
        <div style={{ padding: 16, background: "#fef3c7", borderRadius: 8, color: "#92400e" }}>
          Dostęp do tej funkcji ma tylko administrator.
        </div>
      )}

      {isAdmin && (
        <>
          <p style={{ fontSize: 13, color: "#555" }}>
            Podaj nazwę konta Allegro kontrahenta. Zaimportujemy (współdzielimy) tylko te oferty, których EAN pasuje do Twojego asortymentu. Oferty kontrahenta nie zostaną zmienione.
          </p>
          <input
            type="text"
            value={accountName}
            onChange={(e: any) => setAccountName(e.target.value)}
            placeholder="Nazwa konta kontrahenta (np. NazwaKonta_pl)"
            disabled={!!running}
            style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #ccc", fontSize: 14, boxSizing: "border-box" }}
          />
          {error && <div style={{ color: "#dc2626", fontSize: 13, marginTop: 8 }}>{error}</div>}

          {!running && (
            <button
              onClick={startImport}
              disabled={starting || !accountName.trim()}
              style={{ marginTop: 12, padding: "10px 16px", background: "#0891b2", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 14, opacity: (starting || !accountName.trim()) ? 0.6 : 1 }}
            >
              {starting ? "Uruchamianie…" : "Rozpocznij import"}
            </button>
          )}

          {status && (
            <div style={{ marginTop: 16 }}>
              {running && (
                <>
                  <div style={{ fontSize: 13, marginBottom: 6 }}>
                    Import konta <b>{status.account_name}</b> — {status.status === "pending" ? "przygotowanie…" : "w toku"}
                  </div>
                  <div style={{ background: "#e5e7eb", borderRadius: 6, height: 20, overflow: "hidden" }}>
                    <div style={{ width: `${status.pct || 0}%`, height: "100%", background: "#0891b2", transition: "width 0.5s", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 11 }}>
                      {status.pct || 0}%
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: "#555", marginTop: 6 }}>
                    Sprawdzono {status.done}/{status.total} · dopasowano {status.matched} · szac. czas: {fmtEta(status.eta_seconds)}
                  </div>
                </>
              )}
              {status.status === "done" && (
                <div style={{ padding: 12, background: "#d1fae5", borderRadius: 8, color: "#065f46", fontSize: 14 }}>
                  ✓ Import zakończony. Dopasowano i współdzielono <b>{status.matched}</b> ofert (sprawdzono {status.done}).
                </div>
              )}
              {status.status === "error" && (
                <div style={{ padding: 12, background: "#fee2e2", borderRadius: 8, color: "#991b1b", fontSize: 14 }}>
                  Błąd: {status.error_msg || "nieznany"}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </>
  );

  if (asPanel) {
    return (
      <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 24, maxWidth: 560 }}>
        {renderContent()}
      </div>
    );
  }

  const overlay: any = { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 };
  const box: any = { background: "#fff", borderRadius: 12, padding: 24, width: 480, maxWidth: "90vw", boxShadow: "0 10px 40px rgba(0,0,0,0.2)" };
  return (
    <div style={overlay} onClick={onClose}>
      <div style={box} onClick={(e: any) => e.stopPropagation()}>
        {renderContent()}
        <div style={{ marginTop: 20, textAlign: "right" }}>
          <button onClick={onClose} style={{ padding: "8px 16px", background: "#e5e7eb", border: "none", borderRadius: 8, cursor: "pointer" }}>
            {running ? "Zamknij (import trwa w tle)" : "Zamknij"}
          </button>
        </div>
      </div>
    </div>
  );
}
